import Outlet from "@/common/Outlet";
import { generatePath, Link, Navigate, useMatch } from "react-router";
import { FormattedMessage, FormattedNumber } from "react-intl";
import NavItem from "@/components/NavItem";
import EventJoinButton from "@/components/EventJoinButton";
import { ScrollableScaffoldNav } from "@/components/ScrollableScaffoldNav";
import { MetaLayout } from "@/common/MetaLayout";
import { useEventsMetaTags } from "@/utils/useMetaTags";
import { HeaderContainer } from "@/components/HeaderContainer";
import { Container } from "@/components/Container";
import { EyeIcon, Lock01Icon } from "@/icons";
import { Badge } from "@/kit/ui/badge";
import { useLoginRedirect } from "@/utils/auth";
import { EventLocation } from "@/components/EventLocation";
import { ActivityBanner } from "@/pages/_app.challenges/$slug/_banner";
import { useLocation } from "@/utils/location";
import { graphql, Environment } from "react-relay";
import { LoaderArgs } from "@/utils/route";
import { EventScaffoldQuery as EventScaffoldQueryType } from "./__generated__/EventScaffoldQuery.graphql";
import { preload, usePreloaded } from "@/utils/relay";
import { ActivityNotFound } from "@/components/ActivityNotFound";
import { getOgImageUrl } from "@/utils/routing";
export default function EventScaffold() {
  const {
    query: { eventBySlug: event, viewer },
  } = useEventScaffoldQuery();
  const location = useLocation();
  const to = useLoginRedirect();
  const isTracksPath = useMatch("/challenges/:owner/tracks") !== null;
  const isMembersPath = useMatch("/challenges/:owner/members") !== null;
  const shouldRedirect = !isTracksPath && !isMembersPath;
  const { metaTitle, metaDescription } = useEventsMetaTags(event?.title);
  if (!event) {
    return (
      <ActivityNotFound kind="event">
        {!viewer && (
          <Link to={to}>
            <FormattedMessage defaultMessage="Try logging in to view this page." />
          </Link>
        )}
      </ActivityNotFound>
    );
  }
  if (event.competition?.competition?.slug && shouldRedirect) {
    return (
      <Navigate
        to={generatePath("/challenges/:owner/tracks/:slug", {
          owner: event.slug,
          slug: event.competition.competition.slug,
        })}
        replace
      />
    );
  }
  const eventPath = (...rest: string[]) =>
    rest.length
      ? generatePath("/challenges/:slug/*", {
          slug: event.slug,
          "*": rest.join("/"),
        })
      : generatePath("/challenges/:slug", { slug: event.slug });
  return (
    <MetaLayout
      metaTitle={metaTitle}
      metaDescription={metaDescription}
      metaImageAlt={event.thumbnail && event.shortDescription}
      metaImageUrl={getOgImageUrl(
        { type: "event", slug: event.slug },
        location.origin,
      )}
      hideTitleTemplate
    >
      <HeaderContainer>
        <ActivityBanner title={event.title} banner={event.banner} />
        <div>
          <div className="flex sm:flex-row flex-col-reverse sm:space-x-4 justify-between">
            <div className="flex flex-row justify-start items-center gap-2">
              <h1 className="text-grey-900 text-2xl font-semibold leading-8">
                {event.title}
              </h1>
              <div>
                {event.isPrivate ? (
                  <Badge hasSuffix className="gap-1" size="md" shape="pill">
                    <Lock01Icon className="w-3 h-3" />
                    <FormattedMessage defaultMessage="Private" />
                  </Badge>
                ) : (
                  <Badge hasSuffix className="gap-1" size="md" shape="pill">
                    <EyeIcon className="w-3 h-3" />
                    <FormattedMessage defaultMessage="Public" />
                  </Badge>
                )}
              </div>
            </div>
            <div className="flex flex-row justify-center items-center gap-2">
              {event.viewerCanEdit && (
                <Link to={eventPath("edit")}>
                  <FormattedMessage defaultMessage="Edit" />
                </Link>
              )}
              <EventJoinButton event={event} />
            </div>
          </div>
          <EventLocation location={event} />
        </div>

        <ScrollableScaffoldNav className="-mb-px">
          <div className="flex justify-start items-center w-full">
            <NavItem to={eventPath()} end>
              <FormattedMessage defaultMessage="Overview" />
            </NavItem>
            {event.viewerCanEditMembers && (
              <NavItem to={eventPath("members")}>
                <FormattedMessage defaultMessage="Members" />
              </NavItem>
            )}
            <NavItem to={eventPath("tracks")}>
              <FormattedMessage defaultMessage="Tracks" />
              <Badge>
                <FormattedNumber
                  value={event.competitions.totalCount}
                  notation="compact"
                />
              </Badge>
            </NavItem>
            <NavItem to={eventPath("agenda")}>
              <FormattedMessage defaultMessage="Agenda" />
            </NavItem>
            <NavItem to={eventPath("rules")}>
              <FormattedMessage defaultMessage="Rules" />
            </NavItem>
            <NavItem to={eventPath("discussions")}>
              <FormattedMessage defaultMessage="Discussions" />
            </NavItem>
          </div>
        </ScrollableScaffoldNav>
      </HeaderContainer>
      <Container>
        <Outlet />
      </Container>
    </MetaLayout>
  );
}
const EventScaffoldQuery = graphql`
  query EventScaffoldQuery($slug: String!) {
    viewer @ifAllowed {
      id
    }
    eventBySlug(slug: $slug) {
      id
      slug
      title
      shortDescription
      description
      thumbnail
      createdAt
      banner
      viewerCanEdit: viewerCan(action: UPDATE_EVENT)
      viewerCanEditMembers: viewerCan(action: ADD_EVENT_MEMBER)
      viewerCanAddCompetition: viewerCan(action: ADD_EVENT_COMPETITION)
      isPrivate
      competitions(first: 0) {
        totalCount
      }
      competition {
        competition {
          slug
        }
      }
      ...EventLocationFragment
      ...EventJoinButtonFragment
    }
  }
`;
export const loader = (environment: Environment) => {
  return ({ params: { slug } }: LoaderArgs) => {
    if (!slug) {
      throw new Response("No slug", { status: 500 });
    }
    return preload<EventScaffoldQueryType>(environment, EventScaffoldQuery, {
      slug,
    });
  };
};
const useEventScaffoldQuery = () => usePreloaded<EventScaffoldQueryType>();
